import { openLiveStream } from '../chat/liveStream.js'
/**
 * THE RENTER'S PAGE TALKS TO THE LISTING SERVER, NEVER TO THE DATABASE. (MTR #88.)
 *
 * The quote and enquiry-link pages call Supabase with the anon key because what they read was
 * frozen for them in advance. A listing's price is worked out from the floor and the strategy,
 * which the anon key must not read — so this page asks the server that holds the service key, and
 * gets back only what `listing/publicHome.js` copies out.
 */
import { setMidTermMinimumNights } from '../availability/midTermMinimum.js'
export function createPublicListingApi({ baseUrl, fetchImpl = null,
  /*
    THE STAFF PREVIEW IS THIS SAME CLIENT (#276), pointed at /staff/stay/ and carrying the person's own
    session — `async () => token` — so the preview asks exactly the questions the renter's page asks.
  */
  accessToken = null,
  /*
    THE WEBSITE'S FUNNEL (migration 0075). On the renter's pages only, and only when the page asks:
    `{ ref, referrer, utm }` — who sent her and where from — goes with every step. The answers this
    client already gets say which step she took; nothing waits on it and nothing it does can fail
    her page. Never on a staff preview: a colleague looking is not a renter.
  */
  trackWith = null }) {
  if (!baseUrl) throw new TypeError('the public listing needs the listing server\'s address')
  const base = String(baseUrl).replace(/\/+$/, '')
  /*
    THE ANSWER THAT WAS ALREADY ON ITS WAY (MTR #608).

    `index.html` asks for `/public/stays` before this bundle has finished downloading, because on a
    phone that request otherwise waits nine hundred milliseconds for a bundle it does not need. What
    it leaves behind is a Response promise and nothing else: every decision about the body — the
    404, the company's minimum nights, the errors — stays in `go` below, so the early path and the
    ordinary path cannot drift into answering differently.

    TAKEN ONCE, AND NEVER BY A COLLEAGUE. It is deleted as it is read, so a second call asks for
    itself; and `accessToken` means this is the staff preview, which asks `/staff/stays` and must
    not be handed a renter's answer. A rejected promise is simply not used.
  */
  const takeEarly = () => {
    if (accessToken) return null
    const early = globalThis.__staysEarly ?? null
    if (!early) return null
    try { delete globalThis.__staysEarly } catch { globalThis.__staysEarly = null }
    return Promise.resolve(early).catch(() => null)
  }

  const go = async (path, init = {}, early = null) => {
    /* A question that never answers must not leave "Checking…" on her screen for ever. */
    /* The session first: a signed-out preview must not leave a timer running behind it (Copilot, #278). */
    const token = accessToken ? await accessToken() : null
    if (accessToken && !token) throw Object.assign(new Error('not_signed_in'), { status: 401 })
    const waiting = early ? await early : null
    const ctl = !waiting && typeof AbortController === 'function' ? new AbortController() : null
    const timer = ctl ? setTimeout(() => ctl.abort(), 15_000) : null
    /* Its own timer went with it; what is left is the same Response this line would have made. */
    const res = waiting ?? await (fetchImpl ?? fetch)(`${base}${path}`, {
      ...init,
      ...(ctl ? { signal: ctl.signal } : {}),
      headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}), ...(init.headers ?? {}) },
    })
    let body = null
    try { body = await res.json() } catch { body = null } finally { if (timer) clearTimeout(timer) }
    if (res.status === 404) return { notFound: true }
    if (!res.ok) throw Object.assign(new Error(body?.status ?? `HTTP ${res.status}`), { status: res.status })
    /*
      THE COMPANY'S MID-TERM MINIMUM, AS THE SERVER KNOWS IT (2026-10-07). The renter's page has no
      database; the server's answers carry the number, and her calendar's "Minimum stay" and earliest
      move-out follow it. Staff already hold the setting from their own load, so their preview leaves it.
    */
    if (!accessToken && typeof body?.minimumNights === 'number') setMidTermMinimumNights(body.minimumNights)
    return body
  }
  const stay = accessToken ? '/staff/stay' : '/public/stay'
  const stays = accessToken ? '/staff/stays' : '/public/stays'
  /*
    THE TEAM'S OWN EARLY ANSWER (MTR #657), the colleague's half of `takeEarly` above.

    A renter's search page has its answer asked for by `index.html`, before the bundle is there.
    A colleague's cannot: `/staff/stays` carries their session, which does not exist until the
    product has opened. What it waited for instead was the whole world — the shell draws no screen
    until every table has arrived — so the one request the Listing screen actually draws went out
    at 2,004 ms and landed at 2,549 ms, measured on production 2026-10-21 with the program file
    cached. Nothing about it needed the world; it needed a token, which is there at 430 ms.

    So the shell asks for it as soon as it knows who is signed in and that they are landing here
    (`listing/warmUp.js`), and the screen takes that answer when it mounts.

      · TAKEN ONCE, like the renter's. A colleague who leaves the Listing screen and comes back
        half an hour later must not be shown a half-hour-old list, so the warmed answer is handed
        over to the first caller and forgotten.
      · A WARM-UP THAT FAILED IS NOT AN ERROR ANYBODY SEES. The screen asks for itself and says
        what it always said; nothing new appears on it either way.
      · STAFF ONLY. Without a session this would be the renter's list, which is a different
        question and already answered by `takeEarly`.
  */
  /*
    ONCE PER CLIENT, AND NOT ONCE PER CALL. `warmed` is emptied as the screen takes it, so a flag of
    its own is what stops a second warm-up — and there would be one: the shell's effect runs again
    whenever the session object is replaced, which it is when the token is refreshed. A second
    answer nobody is waiting for would sit here going stale until the colleague came back to this
    screen, and then be handed to them as the list of homes. That is the exact failure this
    mechanism is supposed to avoid.
  */
  let warmed = null
  let everWarmed = false
  const warmStays = () => {
    if (!accessToken || everWarmed) return false
    everWarmed = true
    warmed = go(stays)
    /* Nobody is waiting on it yet, and a rejection nobody is waiting on is a console error. */
    warmed.catch(() => {})
    return true
  }
  const askStays = () => {
    const early = warmed
    warmed = null
    return early ? early.catch(() => go(stays, {}, takeEarly())) : go(stays, {}, takeEarly())
  }
  const tracking = Boolean(trackWith) && !accessToken
  const step = (payload) => {
    if (!tracking) return
    try {
      const body = JSON.stringify({ ref: trackWith.ref ?? null, referrer: trackWith.referrer ?? null, utm: trackWith.utm ?? null, ...payload })
      /* text/plain: a "simple" request, so no preflight — and a keepalive request some browsers would
         refuse to preflight (review of #481). The server reads the body as JSON either way. */
      Promise.resolve((fetchImpl ?? fetch)(`${base}/public/steps`, { method: 'POST', keepalive: true, headers: { 'content-type': 'text/plain;charset=UTF-8' }, body }))
        .catch(() => {})
    } catch { /* her page does not care */ }
  }
  const nightsOf = (checkIn, checkOut) => {
    const n = Math.round((Date.parse(`${checkOut}T00:00:00Z`) - Date.parse(`${checkIn}T00:00:00Z`)) / 86_400_000)
    return Number.isInteger(n) && n > 0 ? n : null
  }
  /* Says the step once the answer is in hand, and hands the answer on untouched. */
  const after = (promise, said) => promise.then((r) => { try { const p = said(r); if (p) step(p) } catch { /* no step */ } return r })
  return {
    /* The tour page a short link opens (MTR #509): the home, and a storage address for an hour. */
    tour: (code) => go(`/public/tour/${encodeURIComponent(code)}`),
    /* The photos and videos we sent her by text or email (FB-0023): an address for each, for an hour. */
    media: (code) => go(`/public/media/${encodeURIComponent(code)}`),
    /* She arrived on our site. Said once per visit by the page that she landed on. */
    visit: () => step({ step: 'visit' }),
    /* The search page (project doc 105): its homes, and their prices for one stay. */
    stays: askStays,
    /* Asked for before any screen exists, when the person is landing on the Listing screen (#657). */
    warmStays,
    stayPrices: ({ checkIn, checkOut }) => after(go(`${stays}/prices`, { method: 'POST', body: JSON.stringify({ checkIn, checkOut }) }),
      (r) => (r?.prices ? { step: 'search', moveIn: checkIn, nights: nightsOf(checkIn, checkOut),
        homesFree: Object.values(r.prices).filter((p) => p?.free === true).length } : null)),
    home: (key) => after(go(`${stay}/${key}`), (r) => (r && !r.notFound ? { step: 'home', home: key } : null)),
    price: (key, { checkIn, checkOut, cleaning }) =>
      after(go(`${stay}/${key}/price`, { method: 'POST', body: JSON.stringify({ checkIn, checkOut, cleaning }) }),
        (r) => (r?.price ? { step: 'price', home: key, moveIn: checkIn, nights: nightsOf(checkIn, checkOut) } : null)),
    /* The days she can pick (#214). No `moveIn` asks which days she can move IN; one asks when she
       could then leave. POST rather than GET because the answer depends on what she has chosen and
       must not be cached by anything between here and the server. */
    openings: (key, { moveIn = null } = {}) =>
      go(`${stay}/${key}/openings`, { method: 'POST', body: JSON.stringify(moveIn ? { moveIn } : {}) }),
    inquiry: (key, payload) =>
      after(go(`/public/stay/${key}/inquiry`, { method: 'POST', body: JSON.stringify(payload) }),
        (r) => (r && !r.notFound ? { step: 'contact', home: key, door: 'form' } : null)),
    /* The web chat (#222/#223): start once with who she is, then write and read with her token. */
    /* An opening of an ambassador's link (Ambassador Program). The answer says nothing either way. */
    click: (key, payload) =>
      go(`/public/stay/${key}/click`, { method: 'POST', body: JSON.stringify(payload) }),
    /* A new conversation is the contact step: through Send inquiry when it carries her dates, else the chat box. */
    chatStart: (payload) => after(go('/public/chat', { method: 'POST', body: JSON.stringify(payload) }),
      (r) => (r?.token ? { step: 'contact', home: payload?.homeKey ?? null,
        door: payload?.checkIn || payload?.duration ? 'form' : 'chat' } : null)),
    chatSend: (token, { message, messageId }) =>
      go(`/public/chat/${encodeURIComponent(token)}/messages`, { method: 'POST', body: JSON.stringify({ message, messageId }) }),
    chatRead: (token) => go(`/public/chat/${encodeURIComponent(token)}/messages`),
    /* 2026-10-09: her email or phone, left after she asked — optional, and only once she chooses. */
    chatContact: (token, { name, email, phone, smsConsent }) => go(`/public/chat/${encodeURIComponent(token)}/contact`,
      { method: 'POST', body: JSON.stringify({ name, email, phone, smsConsent }) }),
    /* #225: her box keeps one connection open — "read now" and "Stayoo is typing" — and says when she types. */
    chatListen: (token, { onEvent, onState }) => openLiveStream({ url: `${base}/public/chat/${encodeURIComponent(token)}/events`,
      fetchImpl, onEvent, onState }),
    chatTyping: (token) => go(`/public/chat/${encodeURIComponent(token)}/typing`, { method: 'POST', body: '{}' }),
  }
}
